<template>
  <div class="seed-wizard">
    <!-- Header with progress -->
    <div class="wizard-header">
      <button class="back-btn" @click="$emit('close')">
        <i class="fas fa-arrow-left"></i> Back
      </button>
      
      <div class="wizard-progress">
        <div class="progress-steps">
          <div 
            v-for="(step, index) in steps" 
            :key="index"
            class="progress-step"
            :class="{ 
              active: currentStep === index + 1, 
              completed: currentStep > index + 1 
            }"
          >
            <div class="step-number">{{ index + 1 }}</div>
            <div class="step-label">{{ step.label }}</div>
          </div>
        </div>
        <div class="progress-bar">
          <div 
            class="progress-fill" 
            :style="{ width: `${(currentStep / steps.length) * 100}%` }"
          ></div>
        </div>
      </div>
    </div>

    <!-- Step content -->
    <div class="wizard-content">
      <!-- Step 1: Test and Publications Selection -->
      <div v-if="currentStep === 1" class="wizard-step">
        <div class="step-header">
          <h3><i class="fas fa-clipboard-list"></i> Test Configuration</h3>
          <p>Select the test and publications for which to generate data.</p>
        </div>

        <div class="form-group">
          <label>Source Test</label>
          <select v-model="config.testId" @change="loadPublications" class="form-control">
            <option value="">Select a test...</option>
            <option v-for="test in availableTests" :key="test._id" :value="test._id">
              {{ test.title }} ({{ test.elements?.length || 0 }} questions)
            </option>
          </select>
        </div>

        <div class="form-group" v-if="config.testId">
          <label>Target Publications</label>
          <div class="publications-grid">
            <div 
              v-for="pub in availablePublications" 
              :key="pub._id"
              class="publication-card"
              :class="{ selected: config.publicationIds.includes(pub._id) }"
              @click="togglePublication(pub._id)"
            >
              <div class="pub-header">
                <i class="fas fa-bullhorn"></i>
                <span class="pub-name">{{ pub.publicationName }}</span>
              </div>
              <div class="pub-details">
                <span class="pub-group">{{ pub.groupId?.groupName || 'No group' }}</span>
                <span class="pub-students">{{ pub.studentsCount || 0 }} students</span>
              </div>
              <div class="pub-checkbox">
                <i :class="config.publicationIds.includes(pub._id) ? 'fas fa-check-circle' : 'far fa-circle'"></i>
              </div>
            </div>
          </div>
          <div class="selection-summary" v-if="config.publicationIds.length > 0">
            {{ config.publicationIds.length }} publication(s) selected • 
            {{ totalStudents }} students total
          </div>
        </div>
      </div>

      <!-- Step 2: Psychometric Parameters -->
      <div v-if="currentStep === 2" class="wizard-step">
        <div class="step-header">
          <h3><i class="fas fa-brain"></i> Psychometric Parameters</h3>
          <p>Configure the quality and characteristics of the generated data.</p>
        </div>

        <div class="params-grid">
          <div class="param-group">
            <h4>Target Reliability</h4>
            <div class="param-slider">
              <label>Cronbach's Alpha: {{ config.targetAlpha }}</label>
              <input 
                type="range" 
                v-model.number="config.targetAlpha" 
                min="0.5" 
                max="0.95" 
                step="0.05"
                class="slider"
              >
              <div class="slider-labels">
                <span>Low (0.5)</span>
                <span>Excellent (0.95)</span>
              </div>
            </div>
            <div class="quality-indicator">
              <span :class="getQualityClass(config.targetAlpha)">
                {{ getQualityLabel(config.targetAlpha) }}
              </span>
            </div>
          </div>

          <div class="param-group">
            <h4>Minimum Discrimination</h4>
            <div class="param-slider">
              <label>Threshold: {{ config.minDiscrimination }}</label>
              <input 
                type="range" 
                v-model.number="config.minDiscrimination" 
                min="0.1" 
                max="0.6" 
                step="0.05"
                class="slider"
              >
              <div class="slider-labels">
                <span>Permissive (0.1)</span>
                <span>Strict (0.6)</span>
              </div>
            </div>
          </div>

          <div class="param-group">
            <h4>Response Consistency</h4>
            <div class="param-slider">
              <label>Noise: {{ config.consistencyNoise }}</label>
              <input 
                type="range" 
                v-model.number="config.consistencyNoise" 
                min="0.1" 
                max="0.8" 
                step="0.1"
                class="slider"
              >
              <div class="slider-labels">
                <span>Very consistent (0.1)</span>
                <span>Chaotic (0.8)</span>
              </div>
            </div>
          </div>

          <div class="param-group">
            <h4>Temporal Variability</h4>
            <div class="param-slider">
              <label>Variation: {{ config.timeVariability }}</label>
              <input 
                type="range" 
                v-model.number="config.timeVariability" 
                min="0.2" 
                max="0.8" 
                step="0.1"
                class="slider"
              >
              <div class="slider-labels">
                <span>Regular (0.2)</span>
                <span>Variable (0.8)</span>
              </div>
            </div>
          </div>
        </div>

        <div class="advanced-options">
          <h4><i class="fas fa-cogs"></i> Advanced Options</h4>
          <div class="options-grid">
            <label class="option-checkbox">
              <input type="checkbox" v-model="config.replaceExisting">
              <span>Replace existing data</span>
            </label>
            <label class="option-checkbox">
              <input type="checkbox" v-model="config.generateTiming">
              <span>Generate realistic response times</span>
            </label>
            <label class="option-checkbox">
              <input type="checkbox" v-model="config.debugMode">
              <span>Debug mode (detailed logs)</span>
            </label>
          </div>
        </div>
      </div>

      <!-- Step 3: Validation and Generation -->
      <div v-if="currentStep === 3" class="wizard-step">
        <div class="step-header">
          <h3><i class="fas fa-rocket"></i> Validation and Generation</h3>
          <p>Review the configuration and launch data generation.</p>
        </div>

        <div class="config-summary">
          <div class="summary-card">
            <h4><i class="fas fa-info-circle"></i> Configuration Summary</h4>
            
            <div class="summary-section">
              <h5>Test and Publications</h5>
              <div class="summary-item">
                <strong>Test:</strong> {{ selectedTestName }}
              </div>
              <div class="summary-item">
                <strong>Publications:</strong> {{ config.publicationIds.length }}
              </div>
              <div class="summary-item">
                <strong>Total Students:</strong> {{ totalStudents }}
              </div>
              <div class="summary-item">
                <strong>Results to Generate:</strong> {{ totalStudents }}
              </div>
            </div>

            <div class="summary-section">
              <h5>Target Quality</h5>
              <div class="summary-item">
                <strong>Cronbach's Alpha:</strong> 
                <span :class="getQualityClass(config.targetAlpha)">
                  {{ config.targetAlpha }} ({{ getQualityLabel(config.targetAlpha) }})
                </span>
              </div>
              <div class="summary-item">
                <strong>Min Discrimination:</strong> {{ config.minDiscrimination }}
              </div>
              <div class="summary-item">
                <strong>Expected Grade:</strong> 
                <span class="grade-badge">{{ expectedGrade }}</span>
              </div>
            </div>
          </div>

          <div class="validation-results" v-if="validationResults">
            <h4>
              <i :class="validationResults.valid ? 'fas fa-check-circle text-success' : 'fas fa-exclamation-triangle text-warning'"></i>
              Configuration Validation
            </h4>
            
            <div v-if="validationResults.valid" class="validation-success">
              <p><i class="fas fa-thumbs-up"></i> Configuration validated successfully!</p>
              <div class="estimation-grid">
                <div class="estimation-item">
                  <span class="label">Estimated time:</span>
                  <span class="value">{{ estimatedTime }}</span>
                </div>
                <div class="estimation-item">
                  <span class="label">Expected quality:</span>
                  <span class="value">{{ validationResults.estimation?.targetQuality?.expectedGrade || 'A-B' }}</span>
                </div>
              </div>
            </div>
            
            <div v-else class="validation-error">
              <p><i class="fas fa-exclamation-circle"></i> {{ validationResults.error }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Navigation -->
    <div class="wizard-navigation">
      <button 
        v-if="currentStep > 1" 
        @click="previousStep" 
        class="nav-btn secondary"
      >
        <i class="fas fa-chevron-left"></i> Previous
      </button>
      
      <div class="nav-spacer"></div>
      
      <button 
        v-if="currentStep < steps.length" 
        @click="nextStep" 
        :disabled="!canProceed"
        class="nav-btn primary"
      >
        Next <i class="fas fa-chevron-right"></i>
      </button>
      
      <button 
        v-if="currentStep === steps.length" 
        @click="generateData" 
        :disabled="!canGenerate || isGenerating"
        class="nav-btn generate"
      >
        <i class="fas fa-rocket"></i>
        {{ isGenerating ? 'Generating...' : 'Generate Data' }}
      </button>
    </div>

    <!-- Generation indicator -->
    <div v-if="isGenerating" class="generation-overlay">
      <div class="generation-spinner">
        <i class="fas fa-seedling spin"></i>
        <h4>Generation in progress...</h4>
        <p>{{ generationStatus }}</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import api from '../../../services/axios.js';

const props = defineProps({
  presetConfig: {
    type: Object,
    default: () => ({})
  }
});

const emit = defineEmits(['close', 'generated']);

// Simple notification system
const showNotification = (message, type = 'info') => {
  console.log(`[${type.toUpperCase()}] ${message}`);
  if (type === 'error') {
    alert(`Error: ${message}`);
  } else if (type === 'success') {
    alert(`Success: ${message}`);
  }
};

// Reactive state
const currentStep = ref(1);
const isGenerating = ref(false);
const generationStatus = ref('');
const validationResults = ref(null);

const steps = [
  { label: 'Test & Publications' },
  { label: 'Parameters' },
  { label: 'Generation' }
];

const config = ref({
  testId: '',
  publicationIds: [],
  targetAlpha: 0.8,
  minDiscrimination: 0.3,
  consistencyNoise: 0.3,
  timeVariability: 0.4,
  replaceExisting: true,
  generateTiming: true,
  debugMode: false
});

const availableTests = ref([]);
const availablePublications = ref([]);

// Computed
const canProceed = computed(() => {
  if (currentStep.value === 1) {
    return config.value.testId && config.value.publicationIds.length > 0;
  }
  return true;
});

const canGenerate = computed(() => {
  return validationResults.value?.valid && !isGenerating.value;
});

const totalStudents = computed(() => {
  return availablePublications.value
    .filter(pub => config.value.publicationIds.includes(pub._id))
    .reduce((sum, pub) => sum + (pub.studentsCount || 0), 0);
});

const selectedTestName = computed(() => {
  const test = availableTests.value.find(t => t._id === config.value.testId);
  return test?.title || 'Not selected';
});

const expectedGrade = computed(() => {
  const alpha = config.value.targetAlpha;
  if (alpha >= 0.9) return 'A';
  if (alpha >= 0.8) return 'B';
  if (alpha >= 0.7) return 'C';
  return 'D';
});

const estimatedTime = computed(() => {
  const students = totalStudents.value;
  if (students < 50) return '< 1 second';
  if (students < 200) return '1-2 seconds';
  return '2-5 seconds';
});

// Methods
const loadTests = async () => {
  try {
    console.log('🔍 Loading tests...');
    console.log('🔍 Token present:', !!localStorage.getItem('editortoken'));
    
    const response = await api.get('/tests/getTests');
    console.log('✅ Tests loaded:', response.data);
    
    availableTests.value = response.data.tests || [];
  } catch (error) {
    console.error('❌ Test loading error:', error);
    console.error('❌ Status:', error.response?.status);
    console.error('❌ Message:', error.response?.data);
    showNotification('Error loading tests', 'error');
  }
};

const loadPublications = async () => {
  if (!config.value.testId) {
    availablePublications.value = [];
    return;
  }

  try {
    console.log('🔍 Loading publications for testId:', config.value.testId);
    
    const response = await api.get('/publications');
    console.log('✅ Publications loaded:', response.data);
    
    const allPubs = response.data.data || [];
    
    availablePublications.value = allPubs
      .filter(pub => {
        // Comparison with testId string or ObjectId
        const pubTestId = pub.testId?._id || pub.testId;
        return pubTestId === config.value.testId;
      })
      .map(pub => {
        console.log('🔍 Publication debug:', {
          name: pub.publicationName,
          groupId: pub.groupId,
          connectedStudentsCount: pub.connectedStudentsCount,
          groupStudentsLength: pub.groupId?.students?.length,
          groupName: pub.groupId?.groupName
        });
        
        return {
          ...pub,
          studentsCount: pub.groupId?.students?.length || pub.connectedStudentsCount || 0
        };
      });
    
    console.log(`📊 ${availablePublications.value.length} publication(s) found for the test`);
    
    // Reset selection if no longer valid
    config.value.publicationIds = config.value.publicationIds.filter(id => 
      availablePublications.value.some(pub => pub._id === id)
    );
    
  } catch (error) {
    console.error('❌ Publication loading error:', error);
    console.error('❌ Status:', error.response?.status);
    console.error('❌ Message:', error.response?.data);
    showNotification('Error loading publications', 'error');
  }
};

const togglePublication = (pubId) => {
  const index = config.value.publicationIds.indexOf(pubId);
  if (index > -1) {
    config.value.publicationIds.splice(index, 1);
  } else {
    config.value.publicationIds.push(pubId);
  }
};

const validateConfiguration = async () => {
  try {
    const response = await api.post('/seed/validate-config', config.value);
    validationResults.value = response.data;
  } catch (error) {
    console.error('Validation error:', error);
    validationResults.value = {
      valid: false,
      error: error.response?.data?.error || 'Validation error'
    };
  }
};

const nextStep = async () => {
  if (currentStep.value === 2) {
    // Validation before final step
    await validateConfiguration();
  }
  currentStep.value++;
};

const previousStep = () => {
  currentStep.value--;
};

const generateData = async () => {
  if (!canGenerate.value) return;

  isGenerating.value = true;
  generationStatus.value = 'Initializing...';

  try {
    generationStatus.value = 'Generating psychometric data...';
    
    const response = await api.post('/seed/generate', config.value);
    
    if (response.data.success) {
      generationStatus.value = 'Generation completed!';
      
      setTimeout(() => {
        emit('generated', {
          ...response.data.data,
          presetName: props.presetConfig?.name || null
        });
      }, 1000);
    } else {
      throw new Error(response.data.error || 'Generation failed');
    }
    
  } catch (error) {
    console.error('Generation error:', error);
    showNotification(error.response?.data?.error || 'Error during generation', 'error');
    isGenerating.value = false;
  }
};

const getQualityClass = (alpha) => {
  if (alpha >= 0.9) return 'quality-excellent';
  if (alpha >= 0.8) return 'quality-good';
  if (alpha >= 0.7) return 'quality-acceptable';
  return 'quality-poor';
};

const getQualityLabel = (alpha) => {
  if (alpha >= 0.9) return 'Excellent';
  if (alpha >= 0.8) return 'Good';
  if (alpha >= 0.7) return 'Acceptable';
  return 'Insufficient';
};

// Watchers
watch(() => props.presetConfig, (preset) => {
  if (preset && Object.keys(preset).length > 0) {
    // Apply preset configuration
    Object.assign(config.value, {
      targetAlpha: preset.targetAlpha || 0.8,
      minDiscrimination: preset.minDiscrimination || 0.3,
      consistencyNoise: preset.consistencyNoise || 0.3,
      timeVariability: preset.timeVariability || 0.4
    });
  }
}, { immediate: true });

// Lifecycle
onMounted(() => {
  loadTests();
});
</script>

<style scoped>
.seed-wizard {
  background: white;
  min-height: 600px;
  position: relative;
}

/* Wizard Header */
.wizard-header {
  padding: 25px 30px;
  border-bottom: 2px solid #e9ecef;
  display: flex;
  align-items: center;
  gap: 30px;
}

.back-btn {
  background: #6c757d;
  color: white;
  border: none;
  padding: 10px 15px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.back-btn:hover {
  background: #545b62;
}

.wizard-progress {
  flex: 1;
}

.progress-steps {
  display: flex;
  justify-content: center;
  gap: 40px;
  margin-bottom: 15px;
}

.progress-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.step-number {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  background: #e9ecef;
  color: #6c757d;
  transition: all 0.3s ease;
}

.progress-step.active .step-number {
  background: #007bff;
  color: white;
}

.progress-step.completed .step-number {
  background: #28a745;
  color: white;
}

.step-label {
  font-size: 0.9rem;
  color: #6c757d;
  transition: color 0.3s ease;
}

.progress-step.active .step-label {
  color: #007bff;
  font-weight: 500;
}

.progress-bar {
  height: 4px;
  background: #e9ecef;
  border-radius: 2px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  transition: width 0.3s ease;
}

/* Wizard Content */
.wizard-content {
  padding: 40px;
  max-width: 800px;
  margin: 0 auto;
}

.step-header {
  text-align: center;
  margin-bottom: 40px;
}

.step-header h3 {
  color: #2c3e50;
  margin-bottom: 10px;
  font-size: 1.5rem;
}

.step-header p {
  color: #6c757d;
  font-size: 1rem;
}

/* Form Elements */
.form-group {
  margin-bottom: 30px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #495057;
}

.form-control {
  width: 100%;
  padding: 12px 15px;
  border: 2px solid #dee2e6;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s ease;
}

.form-control:focus {
  outline: none;
  border-color: #007bff;
}

/* Publications Grid */
.publications-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
}

.publication-card {
  border: 2px solid #dee2e6;
  border-radius: 10px;
  padding: 15px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: white;
  position: relative;
}

.publication-card:hover {
  border-color: #007bff;
  box-shadow: 0 5px 15px rgba(0, 123, 255, 0.1);
}

.publication-card.selected {
  border-color: #007bff;
  background: #f8f9ff;
}

.pub-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}

.pub-name {
  font-weight: 500;
  color: #2c3e50;
}

.pub-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 0.9rem;
  color: #6c757d;
}

.pub-checkbox {
  position: absolute;
  top: 15px;
  right: 15px;
  color: #007bff;
  font-size: 1.2rem;
}

.selection-summary {
  text-align: center;
  color: #007bff;
  font-weight: 500;
  padding: 10px;
  background: #f8f9ff;
  border-radius: 8px;
}

/* Parameters Grid */
.params-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-bottom: 40px;
}

.param-group h4 {
  color: #495057;
  margin-bottom: 15px;
  font-size: 1.1rem;
}

.param-slider {
  margin-bottom: 10px;
}

.param-slider label {
  margin-bottom: 10px;
  font-weight: 500;
}

.slider {
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: #dee2e6;
  outline: none;
  margin-bottom: 10px;
}

.slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #007bff;
  cursor: pointer;
}

.slider-labels {
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
  color: #6c757d;
}

.quality-indicator {
  text-align: center;
  padding: 5px;
}

.quality-excellent { color: #28a745; font-weight: bold; }
.quality-good { color: #17a2b8; font-weight: bold; }
.quality-acceptable { color: #ffc107; font-weight: bold; }
.quality-poor { color: #dc3545; font-weight: bold; }

/* Advanced Options */
.advanced-options {
  background: #f8f9fa;
  padding: 25px;
  border-radius: 10px;
  margin-top: 30px;
}

.advanced-options h4 {
  color: #495057;
  margin-bottom: 20px;
}

.options-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 15px;
}

.option-checkbox {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  padding: 10px;
  border-radius: 8px;
  transition: background 0.2s ease;
}

.option-checkbox:hover {
  background: rgba(0, 123, 255, 0.1);
}

/* Configuration Summary */
.config-summary {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.summary-card {
  background: #f8f9fa;
  padding: 25px;
  border-radius: 15px;
  border: 1px solid #dee2e6;
}

.summary-card h4 {
  color: #495057;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.summary-section {
  margin-bottom: 20px;
}

.summary-section h5 {
  color: #6c757d;
  margin-bottom: 10px;
  font-size: 1rem;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #e9ecef;
}

.grade-badge {
  background: #007bff;
  color: white;
  padding: 3px 8px;
  border-radius: 12px;
  font-weight: bold;
  font-size: 0.9rem;
}

/* Validation Results */
.validation-results {
  background: white;
  padding: 25px;
  border-radius: 15px;
  border: 1px solid #dee2e6;
}

.validation-results h4 {
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.validation-success {
  color: #28a745;
}

.validation-error {
  color: #dc3545;
}

.estimation-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 15px;
  margin-top: 15px;
}

.estimation-item {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.estimation-item .label {
  font-size: 0.9rem;
  color: #6c757d;
}

.estimation-item .value {
  font-weight: bold;
  color: #2c3e50;
}

/* Navigation */
.wizard-navigation {
  padding: 25px 40px;
  border-top: 2px solid #e9ecef;
  display: flex;
  align-items: center;
  gap: 15px;
  background: #f8f9fa;
}

.nav-spacer {
  flex: 1;
}

.nav-btn {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.nav-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.nav-btn.secondary {
  background: #6c757d;
  color: white;
}

.nav-btn.secondary:hover:not(:disabled) {
  background: #545b62;
}

.nav-btn.primary {
  background: #007bff;
  color: white;
}

.nav-btn.primary:hover:not(:disabled) {
  background: #0056b3;
}

.nav-btn.generate {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
  font-size: 1.1rem;
  padding: 15px 30px;
}

.nav-btn.generate:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(40, 167, 69, 0.3);
}

/* Generation Overlay */
.generation-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.generation-spinner {
  text-align: center;
  padding: 40px;
}

.generation-spinner i {
  font-size: 4rem;
  color: #28a745;
  margin-bottom: 20px;
}

.generation-spinner h4 {
  color: #2c3e50;
  margin-bottom: 10px;
}

.generation-spinner p {
  color: #6c757d;
}

.spin {
  animation: spin 2s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Utility Classes */
.text-success { color: #28a745 !important; }
.text-warning { color: #ffc107 !important; }

/* Responsive */
@media (max-width: 768px) {
  .wizard-header {
    padding: 20px;
    flex-direction: column;
    gap: 20px;
  }
  
  .progress-steps {
    gap: 20px;
  }
  
  .wizard-content {
    padding: 20px;
  }
  
  .publications-grid {
    grid-template-columns: 1fr;
  }
  
  .params-grid {
    grid-template-columns: 1fr;
  }
  
  .wizard-navigation {
    padding: 20px;
    flex-direction: column;
  }
  
  .nav-spacer {
    display: none;
  }
}
</style>